import { AppSettings, ChargingSchedule, EvseSettings, FeatureSet, PriceInterval, SiteConfig, SiteControl, SubscriptionPrice } from './db_types';
import { CommandSigning, TeslaAccessType, TeslaCommandReadiness } from './tesla_types';

// API version history
export const API_VERSION_0 = 0 // all app versions before app2.2
export const API_VERSION_1 = 1 // 08/23 app2.2 - introduce account notices
export const API_VERSION_2 = 2 // 11/23 app2.4 - tesla fleet api
export const API_VERSION_3 = 3 // 02/24 app2.6b58 - power flows etc (Test Flight only)
export const API_VERSION_4 = 4 // 02/24 app2.6b59 - move all alerts server-side

// API version (integer) was first introduced in August 2023. The app provides this version
// when connecting, which allows the API to tailor messages to that app where required.
export const API_VERSION = API_VERSION_4

// the API version of the latest app in the app store
export const APP_STORE_API_VERSION = API_VERSION_2

export const COUNTRY_AUSTRALIA = 'AU'
export const COUNTRY_NEW_ZEALAND = 'NZ'

// using Tesla we can go down to 2 amps 3 phase
export const MIN_VALID_AMPS = 2

// J1772 allows up to 80 amps
export const MAX_VALID_AMPS = 80

// min/max amps when we don't know any better
export const DEFAULT_MIN_AMPS = 6
export const DEFAULT_MAX_AMPS = 48

export type StripeSubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'paused'
  | 'trialing'
  | 'unpaid';

export enum NamedAction {
  ResumeCharging = 'resume-charging', //resume charging now from the holding state
  ResumeControl = 'resume-control', //revert to auto mode from external mode
}

export interface TokenFields {
  user: {
    id: string
    email: string
    isAdmin?: boolean
  }
  impersonatingUser?: { //the user doing the impersonation, ie the admin
    id: string
    email: string
  }
  // tcheck should match the user.tcheck value. This allows invalidating all tokens
  // by changing user.tcheck to a new value.
  // When impersonating it should be set to the target user's tcheck value. This is
  // fairly useless, but impersonating tokens only last 1 hour so no need to invalidate.
  tcheck?: string
  // pcheck contains the first 16 chars from the user's password hash, and should be verified
  // if present. This allows invalidating tokens by resetting the user's password.
  // pcheck is used with pre-Cognito tokens and is deprecated by tcheck
  pcheck?: string
  // standard claims
  iat: number //token issue time (unix time)
}

export type AppVariant = 'ios' | 'android' | 'web'

export enum TokenVersion {
  v1 = 'v1', //pre-cognito. uses tcheck field
  v2 = 'v2', //since cognito. uses pcheck field
}

export interface AppVersionInfo {
  variant?: AppVariant
  version?: string //eg "1.8" (not supplied on web)
  build?: string //eg "37" (not supplied on web)
  gitBuildInfo?: string
}

// common fields to some replies
export interface ReplyBase {
  header?: string //alert popup header
  message?: string //alert message
}

export interface SignInRequest {
  idToken: string // a Cognito ID token JWT
  appVersion: AppVersionInfo
}

// a sign in reply contains either:
//  - an API token, which means the app user account exists, or
//  - a flag indicating that sign up is incomplete
export interface SigninReply {
  apiToken: string
  signupIncomplete?: true
}

export interface CompleteSignupRequest {
  firstName: string
  lastName: string
  country: string
  timezone: string
  appVersion: AppVersionInfo
}

export interface AppOpenedRequest extends AppVersionInfo {
  fromBackground?: boolean
}

// deprecated - the old sign up method
export interface SignupRequest {
  firstName: string
  lastName: string
  email: string
  password: string
  country: string
  timezone: string
  appVersion?: AppVersionInfo //added in 2.0
  supportsBilling?: true //must be set once billing is enabled
}

// deprecated
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginReply {
  token: string
}

export interface PlanPrice {
  amountCents: number // eg 700
  amountDisplay: string // eg "$7.00", includes symbol
  interval: PriceInterval //'month' or 'year'
}

// a plan with extra info specific to the current user
export interface MyPlan {
  planId: string
  name: string
  description?: string
  featureList: string[]
  currency: string
  prices: PlanPrice[]
  subscription?: MySubscription // set if user is subscribed to this plan
  canSubscribe?: boolean // can the user subscribe (without a trial) to this plan?
  canStartTrial?: boolean
  newTrialDays?: number // number of trials days the user can have if starting
  openPortalDesc?: string // text to show on open portal button
  canSwitchInterval?: boolean
}

export interface GetMyPlansReply {
  plans: MyPlan[]
  currency: string
  hidePlanNotices: boolean
  footerText?: string
}

export interface GetAccountReply {
  email: string
  firstName: string
  lastName: string
  planName?: string //unset if BILLING_ENABLED is false
}

export interface SubscriptionInfo {
  planId: string
  price: SubscriptionPrice
  interval?: PriceInterval
  currency?: string
  stripeStatus?: StripeSubscriptionStatus
  isValid: boolean //is the subscription valid, ie will be provisioned
  isTrial: boolean //is this currently a free trial?
  trialEndAt?: string //ISO datetime, if currently trialing, when it ends
  trialDaysRemaining?: number //if trialing, whole days remaining
  currentPeriodEnd?: string //ISO datetime, end of current billing period
  hasStripeSubscription: boolean
  hasPaymentMethod: boolean | null //the customer or subscription has a payment method
  cancelAt?: string //ISO datetime, subscription will cancel at this time
  cancelAtPeriodEnd?: boolean //DEPRECATED use cancelAt, subscription will cancel at period end
  stripeScheduleId?: string //if set, id of stripe subscription schedule
  statusError?: string //eg 'Unpaid', 'Overdue'
  canCancel?: boolean //can the user cancel this subscription?
}

export interface MySubscription extends SubscriptionInfo {
  renewalDesc?: string //eg 'Your plan is billed monthly, and renews on 4 July.'
  confirmCancelText?: string //eg "Your subscription will be cancelled on 4 July. It will remain active until then."
}

export interface GetSubscriptionReply {
  email: string
  subscription: SubscriptionInfo | null
  appSettings: AppSettings
  newTrialDays: number //number of days allowed for a new free trial
}

export interface StartFreeTrialRequest {
  planId?: string //if not supplied, default to standard plan
  enableSolarTracking?: boolean
  screenName?: string
}

export interface ChangeSubscriptionIntervalRequest {
  interval: PriceInterval
}

// deprecated by change email flow
export interface UpdateEmailRequest {
  email: string
  password: string
}

export interface ChangeEmailReply {
  token: string //a new API token for the new email
}

export interface UpdateAccountRequest {
  firstName?: string
  lastName?: string
}

export interface SendAlphaEssCodeRequest {
  sysSn: string
  checkCode: string
}

export interface SelectEnergySiteRequest {
  energySiteId: string
}

export interface CreateEvseRequest {
  ocppId: string
  settings: EvseSettings
}

export interface GetConnectTeslaReply {
  title?: string //page title eg "Update Tesla Connection"
  isReconnect: boolean
  heading?: string //displayed with emphasis at the top
  notes: string[]
  instructions: string[] //displayed as bullet list
  helpNotes: string[] //displayed below faintly
  connectButtonText: string
  authUrl: string //the tesla oauth URL
  learnMoreUrl: string
}

// the old API way
export interface AddTeslaRequest {
  refresh_token: string //sso refresh token
}

// the new Fleet API way
export interface ConnectTeslaRequest {
  // caller must supply either a code or a refresh token
  code?: string //auth code provided by Tesla redirect
  refreshToken?: string //sso refresh token
}

export interface ConnectTeslaReply extends ReplyBase {
  showPairing: boolean //take the user to the pairing screen
}

export interface DeleteTeslaReply {
  showRevokeAuth: boolean
}

export enum DataDownloadResolution {
  Monthly = 'monthly',
  Daily = 'daily',
  Interval = 'interval', //15 minute
}

export interface DataDownloadPageReply {
  firstStatsTime: string | null //ISO datetime
  defaultResolution: DataDownloadResolution
  allowedByPlan: boolean
  planNotice?: string
  notes: string[] //shown as paragraphs
  instructions: string[] //shown as bullet list
  helpNotes: string[]
}

export interface DownloadDataRequest {
  resolution: DataDownloadResolution
  startDate: string //YYYY-MM-DD
  endDate: string //YYYY-MM-DD
}

// values are strings as they have been formated to 1 decimal place
export interface DownloadDataTotals {
  chargedKwh: string
  atHomeKwh: string
  awayFromHomeKwh: string
  // below totals are at home
  fromSolarKwh: string
  fromBatteryKwh: string
  fromGridKwh: string
}

// internal type that holds the data for download
export interface DownloadData {
  resolution: DataDownloadResolution
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  startStr: string // eg "21 Aug 2023" or "Aug 2023"
  endStr: string // eg "21 Aug 2023" or "Aug 2023"
  rows: any[]
}

export enum TeslaPairingStatus {
  Paired = 'paired',
  Unpaired = 'unpaired',
  NotRequired = 'not required',
  Unknown = 'unknown',
}

export interface GetTeslaPairingReply {
  pairingStatus: TeslaPairingStatus
  statusMessage?: string //shown in bold at the top, optional
  notes: string[] //always shown
  desktopNotes: string[] //shown on desktop if showPairingLink is true
  instructions: string[] //shown on mobile if showPairingLink is true
  showPairingLink: boolean
  showTestPairing: boolean
  showSendPairingLink: boolean
  keyPairingUrl: string
  learnMoreUrl: string
}

export interface TestTeslaPairingReply extends ReplyBase {
  pairingStatus?: TeslaPairingStatus //only set if we were able to test it
  commandReadiness?: TeslaCommandReadiness
}

export interface GetRevokeTeslaAuthReply {
  heading?: string //displayed with emphasis at the top
  notes: string[]
  instructions: string[] //displayed as bullet list
  helpNotes: string[] //displayed below faintly
  revokeAuthText: string //name on link / button
  revokeAuthUrl: string //the URL to tesla.com
  learnMoreUrl: string
}

export enum PlanCta {
  StartFreeTrial = 'StartFreeTrial',
  EnterPaymentDetails = 'EnterPaymentDetails',
  SubscribePaidPlan = 'SubscribePaidPlan',
}

export interface PlanNotice {
  header: string
  content: string
  cta: PlanCta
  newTrialDays?: number //number of days for new free trial
}

export interface NamedActionRequest {
  namedAction: NamedAction
}

export interface Notice {
  noticeId: string
  content: string
  color?: 'warning' | 'primary' //values match ionic app colors
  detail?: boolean //notice has detail, can view notice page
  route?: string //app route eg '/settings/solar'
  learnMoreUrl?: string //typically link to our website
}

export interface GetSiteFullReply {
  appSettings: AppSettings
  config: SiteConfig
  settings: SiteControl
  solarRefreshCycleSec: number | null
  newTrialDays: number
  featureSet: FeatureSet
  planNotice?: PlanNotice
  notices?: Notice[]
  signupIncomplete?: true
}

export interface GetScheduleReply {
  schedule: ChargingSchedule
  settings: SiteControl
  config: SiteConfig
  notes: string[] //notes displayed at bottom of schedule page
  chargeNotes: string[] //notes for the charge period page
  blockoutNotes: string[] //notes for the blockout period page
  minutesStep: number //granularity of start/end times in minutes, for app
  learnMoreUrl?: string
}

export interface UpdateAmberApiTokenRequest {
  apiToken: string
}

export interface SuggestedOcppIdReply {
  ocppId: string
}

export interface TeslaEnergySite {
  name: string
  energySiteId: string
  hasBattery: boolean
  hasLoadMeter: boolean
}

export interface TeslaAccountProducts {
  vehicles: {
    id_s: string
    name: string // can be blank
    vin: string
    api_version: number
    access_type: TeslaAccessType | null
    command_signing: CommandSigning
  }[]
  // both powerwalls and solar-only sites
  energySites: TeslaEnergySite[]
}

export interface GetTeslaAuthUrlReply {
  url: string
}

export interface GetTeslaAccountReply extends TeslaAccountProducts {
  selectedVehicleIdS: string | null
  selectedVehicleName: string | null //deprecated
  selectedEnergySiteId?: string | null
  tokenInvalid?: boolean //if true, no other data will be returned
  // below fields added app 2.4, api version 2, Nov 2023
  showPairing?: boolean //allow the user to navigate to the pairing screen
  error?: string //account error to display to user
  showReconnect?: boolean //show a reconnect button, eg when using old api or need scope change
  reconnectButtonText?: string
  // below fields added for app 2.9, May 2024
  showManagePermissions?: boolean //show a button to open the Tesla app access page
  managePermissionsUrl?: string //the URL to the Tesla app access page
  disableVehicleSelect?: boolean //disable the vehicle selection function, eg when no scope
  disableEnergySiteSelect?: boolean //disable the energy site selection, eg when no scope
  notes?: string[] //notes to show user, may relate to error
}

export interface SelectTeslaRequest {
  id_s: string
}

export interface UpdateCountryRequest {
  country: string //eg AU
  timezone: string //eg Australia/Sydney
}

export interface SiteMeterPush {
  // obtain your API key from the app: My Equipment -> Solar Data Source
  apiKey?: string

  // DEPRECATED - use apiKey instead
  siteId?: string

  // timestamp of meter data (milliseconds since epoch)
  // this field should be supplied if the solar data has a reliable timestamp,
  // otherwise it should be left unset
  tsms?: number

  // set this field only if there was an error obtaining the meter data
  error?: string

  // provide the meter data, unless there was an error
  siteMeters?: {
    // if solar is present, provide the following field
    production_kw?: number

    // if a consumption meter is present, the following fields should be set
    net_import_kw?: number //grid import, negative means export
    consumption_kw?: number //total site consumption

    // if accumulated import/export energy is available, set the following fields
    imported_kwh?: number
    exported_kwh?: number

    // if a battery is present, provide the following fields
    battery_discharge_kw?: number //discharge power, negative means charging
    battery_soc?: number //eg 0.5 = 50%
    battery_discharge_kwh?: number //cumulative energy (optional)
    battery_charge_kwh?: number //cumulative energy (optional)
    battery_energy_kwh?: number //amount of energy in the battery (optional)
  }
}

export interface StatsSummaryReply {
  timezone: string | null //site timezone (if known)
  firstStatsTime: string | null //ISO date time. null if no stats
}

export enum StatsViewPeriod {
  Day = 'Day',
  Month = 'Month',
  Year = 'Year',
}

export interface StatsViewRequest {
  period: StatsViewPeriod
  date: string //ISO, format depends on the period, eg:
               // '2022-10-30', for Day view
               // '2022-10', for Month view
               // '2022', for Year view
}

export interface StatsViewReply {
  period: StatsViewPeriod
  xLabels: string[] //x-axis labels
  // all series values are kWh
  fromSolar: number[]
  fromBattery: number[]
  fromGrid: number[]
  fromOther: number[]
  totalFromSolar: number
  totalFromBattery: number
  totalFromGrid: number
  totalFromOther: number
  totalCharged: number
  selfPoweredPercent: number | null //eg 75, null if no charging
  hasSolar: boolean //was solar present for any of the period?
  hasBattery: boolean //was a battery present for any of the period?
}

// returns milliseconds since epoch
export function getTsms(): number {
  return new Date().getTime()
}

export interface FcmActionReport {
  actionId: string //eg 'tap'
  fcmData: any //various data supplied by FCM, varies by platform
}

export interface CreateCheckoutLinkRequest {
  planId?: string
  interval?: PriceInterval
}

export interface CreateCheckoutLinkReply {
  checkoutLink: string //stripe checkout session URL
  successUrl: string //the success page URL
  successMessage: string //message to display after successful checkout
}

export interface CreateStripeLinkReply {
  link: string //stripe portal URL
}

// reply used to populate the notice page
export interface NoticeDetailsReply {
  noticeId: string
  title: string //page title
  sections: {
    heading?: string
    p?: string[] //text paragraphs
    li?: string[] //list items
  }[]
  learnMoreUrl?: string // optional link to external site to learn more, eg our website
  acceptCta?: string //text on the (optional) accept CTA button eg "Switch to new control system"
}

export interface AcceptNoticeReply {
  message: string
}
